<div class="main-view space-y-2 md:space-y-4">
  <app-page-surface-header titleIcon="settings"
    title="LLM Task Configuration"
    subtitle="Model and tuning per AI task. Published versions are immutable — editing one creates a new draft. ai-service falls back to its compiled defaults if this is unreachable."
  >
    <ng-container ngProjectAs="[pageHeaderActions]">
      <!-- Shown only on an environment with nothing configured: a fresh deployment starts empty,
           and until now the page gave no way to populate it. -->
      @if (!loading && !records.length) {
        <button
          type="button"
          class="btn btn-primary inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium disabled:opacity-50"
          [disabled]="seeding"
          (click)="seedDefaults()"
        >
          {{ seeding ? 'Creating…' : 'Create default configs' }}
        </button>
      }
    </ng-container>
  </app-page-surface-header>

  @if (error) {
  <div
    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
    role="alert"
  >
    {{ error }}
  </div>
  }

  @if (notice && !error) {
  <output
    class="block rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300"
  >
    {{ notice }}
  </output>
  }

  @if (draft) {
  <div
    class="rounded-2xl border border-gray-200/80 bg-white/40 p-4 shadow-sm dark:border-[#191e3a] dark:bg-dark/10 md:p-6"
  >
    <h2 class="mb-4 text-base font-semibold text-gray-900 dark:text-white">{{ draft.task_code }}</h2>
    <label class="mb-3 block text-sm">
      <span class="mb-1 block font-medium text-gray-900 dark:text-white">Model</span>
      <select class="form-select w-full rounded-xl" [(ngModel)]="draft.model_id">
        @for (m of models; track m['id']) {
        <option [ngValue]="m['id']">{{ m['display_name'] }}</option>
        }
      </select>
    </label>
    <label class="mb-3 block text-sm">
      <span class="mb-1 block font-medium text-gray-900 dark:text-white">Max tokens</span>
      <input
        class="form-input w-full rounded-xl"
        type="number"
        min="1"
        [(ngModel)]="draft.max_tokens"
      />
    </label>
    <label class="mb-4 block text-sm">
      <span class="mb-1 block font-medium text-gray-900 dark:text-white"
        >Temperature (0–1, blank to leave unset)</span
      >
      <input
        class="form-input w-full rounded-xl"
        type="number"
        min="0"
        max="1"
        step="0.1"
        [(ngModel)]="draft.temperature"
      />
    </label>
    <div class="flex gap-2">
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full bg-[#303030] px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-50 dark:bg-gray-900 dark:hover:bg-black"
        [disabled]="saving"
        (click)="save()"
      >
        {{ saving ? 'Saving…' : 'Save draft' }}
      </button>
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="cancel()"
      >
        Cancel
      </button>
    </div>
  </div>
  }

  <app-list-module-surface
    [config]="listSurfaceConfig"
    [filters]="listFilters"
    [loading]="loading"
    [meta]="paginationMeta"
    [rowCount]="pagedRecords.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    <app-list-module-table
      listTable
      variant="surface"
      [columns]="columns"
      [rows]="pagedRecords"
      [loading]="loading"
      [emptyMessage]="emptyMessage"
      rowTrackKey="id"
    >
      <ng-template listCellDef="task_code" let-row="row">
        <span class="font-mono text-xs text-gray-900 dark:text-white">{{ row['task_code'] }}</span>
      </ng-template>

      <ng-template listCellDef="version" let-row="row">
        v{{ row['version'] }}
      </ng-template>

      <ng-template listCellDef="model" let-row="row">
        {{ modelName(row) }}
      </ng-template>

      <ng-template listCellDef="temperature" let-row="row">
        {{ formatTemperature(row) }}
      </ng-template>

      <ng-template listCellDef="max_tokens" let-row="row">
        {{ row['max_tokens'] }}
      </ng-template>

      <ng-template listCellDef="status" let-row="row">
        <div
          class="inline-flex items-center rounded-full py-1 px-3"
          [ngClass]="statusBadgeClass(row['status'])"
        >
          {{ statusLabel(row['status']) }}
        </div>
      </ng-template>

      <ng-template listCellDef="actions" let-row="row">
        <div class="inline-flex items-center justify-end gap-1">
          @if (isDraft(row)) {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
            title="Edit"
            aria-label="Edit"
            (click)="edit(row)"
          >
            <icon-edit class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-blue-500 hover:bg-blue-500/10"
            title="Publish"
            aria-label="Publish"
            (click)="publish(row)"
          >
            <icon-send class="h-4 w-4" />
          </button>
          } @else {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
            title="New version"
            aria-label="New version"
            (click)="newVersion(row)"
          >
            <icon-plus-circle class="h-4 w-4" />
          </button>
          }
        </div>
      </ng-template>
    </app-list-module-table>
  </app-list-module-surface>
</div>
